import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Users, MapPin, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CaseStudies() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        setCaseStudies(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching case studies:', error);
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  return (
    <div data-testid="case-studies-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 data-testid="case-studies-title" className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-6">
            CASE <span className="text-primary">STUDIES</span>
          </h1>
          <p data-testid="case-studies-subtitle" className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Real-world success stories showcasing our impact on energy efficiency and building performance
          </p>
        </motion.div>

        {/* Case Studies Grid */}
        {loading ? (
          <div data-testid="loading-spinner" className="text-center py-24">
            <div className="inline-block w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : caseStudies.length > 0 ? (
          <div data-testid="case-studies-grid" className="space-y-8">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`case-study-${study.slug}`}
                className="glass p-8 md:p-12 hover:bg-white/5 transition-all hover:glow-green"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Client Info */}
                  <div className="lg:col-span-1">
                    {study.image_url && (
                      <img 
                        src={study.image_url} 
                        alt={study.client} 
                        className="w-full h-48 object-cover mb-6"
                      />
                    )}
                    <h3 className="text-2xl font-bold mb-4">{study.client}</h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <MapPin size={16} className="mr-2 text-primary" />
                        {study.location}
                      </div>
                    </div>
                  </div>

                  {/* Study Details */}
                  <div className="lg:col-span-2">
                    <h2 className="text-3xl font-bold mb-4 group-hover:text-primary transition-colors">
                      {study.title}
                    </h2>
                    <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                      {study.summary}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="text-sm font-mono uppercase tracking-wider text-primary mb-2">Challenge</h4>
                        <p className="text-sm text-muted-foreground">{study.challenge}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-mono uppercase tracking-wider text-primary mb-2">Solution</h4>
                        <p className="text-sm text-muted-foreground">{study.solution}</p>
                      </div>
                    </div>

                    {/* Results */}
                    {study.results && Object.keys(study.results).length > 0 && (
                      <div>
                        <h4 className="text-sm font-mono uppercase tracking-wider text-primary mb-4">Results</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {Object.entries(study.results).map(([key, value]) => (
                            <div key={key} className="bg-accent/20 p-4 border border-white/5">
                              <div className="text-2xl font-bold text-primary mb-1">{value}</div>
                              <div className="text-xs text-muted-foreground uppercase">{key.replace('_', ' ')}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div data-testid="no-case-studies-message" className="text-center py-24">
            <p className="text-muted-foreground text-lg">Case studies coming soon. Check back for real-world success stories!</p>
          </div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          data-testid="case-studies-cta"
          className="mt-16 text-center glass p-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 uppercase">
            READY TO BE OUR <span className="text-primary">NEXT SUCCESS STORY?</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Contact us today to discuss how we can help transform your building's energy performance.
          </p>
          <a
            href="/contact"
            data-testid="start-project-btn"
            className="inline-flex items-center px-8 py-4 bg-primary text-black font-mono uppercase tracking-wider text-sm font-bold hover:bg-primary/90 transition-all hover:glow-green group"
          >
            START YOUR PROJECT
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
          </a>
        </motion.div>
      </div>
    </div>
  );
}
